import { useState, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { useHelper } from './useHelper'
import QuizService from '@/services/QuizService'
import { uploadFaqUrlAndDocument } from '@/store/knowledge/actions'
import {
  saveQuizQuestionsBasedOnFile,
  updateQuiz as updateQuizAction,
} from '@/store/quizzes/actions'

const quizService = new QuizService()

interface DocumentFile extends File {
  sizeError?: boolean
  progress?: number
  isUploading?: boolean
  isUploaded?: boolean
  isError?: boolean
  message?: string
}

interface UseQuizImportOptions {
  mode: 'import-quiz' | 'import-question-bank'
}

export const useQuizImport = ({ mode }: UseQuizImportOptions) => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { t } = useTranslation('translation')
  const { showToaster } = useHelper()

  const [documents, setDocuments] = useState<DocumentFile[]>([])
  const [isFileUploading, setIsFileUploading] = useState(false)
  const [selectedFileType, setSelectedFileType] = useState('')
  const [selectedFile, setSelectedFile] = useState<any>(null)
  const [selectedFileName, setSelectedFileName] = useState('')
  const [selectedUploadType, setSelectedUploadType] =
    useState('customtemplates')

  const queryParams = new URLSearchParams(window.location.search)

  const handleFileSelection = useCallback(
    (files: File[], type: string) => {
      setIsFileUploading(false)
      setSelectedFileType(type)

      if (files.length > 0) {
        setSelectedFileName(files[0].name)
      }

      const totalDocuments = documents.length + files.length
      if (totalDocuments > 1) {
        showToaster(t('You can upload only single file.'))
        return
      }

      const processedFiles = files.map((file: any) => {
        file.sizeError = file.size / (1024 * 1024) > 10
        return file
      })

      setDocuments([...documents, ...processedFiles])
    },
    [documents, showToaster, t],
  )

  const handleFileRemove = useCallback((index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleRadioChange = useCallback((value: string) => {
    setSelectedUploadType(value)
    setSelectedFile('')
  }, [])

  const createExtractionJob = useCallback(
    async (documentId: string) => {
      const query = new URLSearchParams(location.search)
      const group = query.get('group')

      try {
        const response = await quizService.createQuizExtractionJob(documentId)
        const jobId = response?.data?.data?.quiz_extraction_job?._id

        if (jobId) {
          if (mode === 'import-quiz') {
            history.push(
              `/import-questions-bank/processing/${jobId}?through=create-quiz&quizId=${queryParams.get('quizId')}`,
              {
                quizId: queryParams.get('quizId'),
                selectedFileType,
                selectedFile,
                selectedFileName,
                through: 'create-quiz',
              },
            )
          } else {
            // import-question-bank mode - no quizId needed
            const groupQuery = group ? `?group=${group}` : ''
            history.push(
              `/import-questions-bank/processing/${jobId}${groupQuery}`,
              {
                group,
                school: '',
              },
            )
          }
        } else {
          showToaster(t('Failed to start extraction job.'))
          setIsFileUploading(false)
        }
      } catch (error) {
        console.error('Error creating extraction job:', error)
        showToaster(t('Something went wrong while processing the document.'))
        setIsFileUploading(false)
      }
    },
    [
      location,
      history,
      mode,
      queryParams,
      selectedFileType,
      selectedFile,
      selectedFileName,
      showToaster,
      t,
    ],
  )

  const saveToDatabase = useCallback(async () => {
    if (documents.length === 0) {
      showToaster(t('Please choose document before proceeding.'))
      return
    }

    setIsFileUploading(true)

    const oldDocuments = [...documents]
    const query = new URLSearchParams(location.search)
    const group = query.get('group')

    if (documents.length > 0) {
      oldDocuments[0].isUploading = true
    }

    const uploadedDocument: any = await dispatch(
      uploadFaqUrlAndDocument(
        {
          file: documents[0],
          file_name: documents[0].name,
          faqType: 'quiz',
          group,
        },
        (event: any) => {
          if (oldDocuments.length > 0) {
            oldDocuments[0].progress = Math.round(
              (100 * event.loaded) / event.total,
            )
            setDocuments([...oldDocuments])
          }
        },
      ),
    )

    if (documents.length > 0) {
      if (uploadedDocument && uploadedDocument.data.status.code !== 200) {
        oldDocuments[0].isUploaded = false
        oldDocuments[0].isError = true
        oldDocuments[0].isUploading = false
        oldDocuments[0].message = uploadedDocument.status.message
        setDocuments([...oldDocuments])
      } else {
        createExtractionJob(uploadedDocument.data.data.document._id)
      }
    }
  }, [documents, dispatch, location, showToaster, t, createExtractionJob])

  const updateQuiz = useCallback(
    async (
      questions: any[],
      content: any = null,
      documentId: any = null,
      documentType: any = null,
    ) => {
      const query = new URLSearchParams(location.search)
      const group = query.get('group')

      const quizArray: any = { questions: [] }
      questions.forEach((element: any) => {
        const quiz = {
          ...element,
          id: element.id ? element.id : uuidv4(),
        }

        if (quiz.ideal_answer === null) {
          quiz.ideal_answer = ''
        }

        quizArray.questions.push({
          ...quiz,
          text: quiz.text.replaceAll('\n', '<br>'),
        })
      })

      const quizId = queryParams.get('quizId')

      if (selectedFileType === 'customtemplates') {
        const state: any = {
          selectedFileType: true,
          documentContent: content,
          quizArray,
          selectedFile: selectedFile,
          documentId: documentId,
          documentType,
        }

        if (mode === 'import-question-bank') {
          state.createFromTestFile = true
          state.group = group
          const groupQuery = group ? `?group=${group}` : ''
          history.push(
            `/import-questions-bank/questions-review${groupQuery}`,
            state,
          )
        } else {
          history.push(`/quizzes/edit/${quizId}`, state)
        }
        return
      }

      if (mode === 'import-question-bank') {
        // For import-question-bank mode, redirect to question bank editor
        const groupQuery = group ? `?group=${group}` : ''
        history.push(`/import-questions-bank/questions-review${groupQuery}`, {
          selectedFileType: false,
          quizArray,
          createFromTestFile: true,
          group,
        })
      } else {
        // For import-quiz mode, update the quiz and redirect to quiz editor
        try {
          await dispatch(updateQuizAction(quizId, quizArray))
          history.push(`/quizzes/edit/${quizId}`, {
            selectedFileType: false,
          })
        } catch (e) {
          console.error('Error updating quiz:', e)
        }
      }
    },
    [
      location,
      queryParams,
      selectedFileType,
      selectedFile,
      mode,
      dispatch,
      history,
    ],
  )

  const uploadFile = useCallback(async () => {
    if (documents.length === 0) {
      showToaster(t('Please choose documents or add url before proceeding.'))
      return
    }

    const formData = new FormData()
    formData.append('file', documents[0])
    formData.append('filepath', documents[0].name)

    try {
      const questionsBasedOnFileResponse: any = await dispatch(
        saveQuizQuestionsBasedOnFile(formData),
      )
      updateQuiz(questionsBasedOnFileResponse.questions)
    } catch (error) {
      showToaster(t('Oops! Could not upload.'))
    }
  }, [documents, dispatch, showToaster, t, updateQuiz])

  const handleImport = useCallback(() => {
    if (selectedFileType === 'customtemplates') {
      saveToDatabase()
    } else {
      uploadFile()
    }
  }, [selectedFileType, saveToDatabase, uploadFile])

  return {
    documents,
    isFileUploading,
    selectedFileType,
    selectedUploadType,
    handleFileSelection,
    handleFileRemove,
    handleRadioChange,
    handleImport,
  }
}
